import React, { useEffect, useState } from 'react';
import CustomSlider from '../child/CustomSlider';
import { ChevronDown } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { LLM_CONFIG } from '../../utils/assistants/llm_config';
import { appendToAssistantDetail } from '../../store/app/assistantSlice';
import { getAllFiles } from '../../utils/api/FileAPI';
import { useNavigate } from 'react-router-dom';

function LLMComponent() {
  const assistantDetails = useSelector(
    (state) => state?.assistants?.activeDetail?.details
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Extract values safely from Redux state with fallbacks
  const reduxModel = assistantDetails?.model || {};
  const initialProvider = reduxModel?.provider;
  const initialModel = reduxModel?.model;
  const initialTemperature =
    typeof reduxModel?.temperature === 'number' ? reduxModel?.temperature : 0.5;
  const initialMaxTokens =
    typeof reduxModel?.maxTokens === 'number' ? reduxModel?.maxTokens : 50;

  const providers = Object.keys(LLM_CONFIG);
  const defaultProvider = providers.includes(initialProvider)
    ? initialProvider
    : providers[0];

  // Local states
  const [selectedProvider, setSelectedProvider] = useState(defaultProvider);
  const modelEntries = Object.entries(
    LLM_CONFIG[selectedProvider]?.models || {}
  );
  const availableModels = Object.values(
    LLM_CONFIG[selectedProvider]?.models || {}
  );
  const defaultModel = availableModels?.includes(initialModel)
    ? initialModel
    : availableModels[0];

  const [selectedModel, setSelectedModel] = useState(defaultModel);
  const [temperature, setTemperature] = useState(initialTemperature);
  const [maxTokens, setMaxTokens] = useState(
    initialMaxTokens >= 50 ? initialMaxTokens : 50
  );
  const [maxTokensInput, setMaxTokensInput] = useState(String(maxTokens));
  const [files, setFiles] = useState({});
  const [fileLoading, setFileLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileSize, setFileSize] = useState(0);

  useEffect(() => {
    if (!files?.files) return;

    const totalBytes = selectedFiles.reduce((sum, id) => {
      const fileObj = files.files.find((f) => f.id === id);
      return sum + (fileObj?.bytes || 0);
    }, 0);

    setFileSize(totalBytes);
  }, [selectedFiles, files]);


  // Pre-select existing fileIds from assistantDetails
  useEffect(() => {
    const existingFileIds =
      assistantDetails?.model?.knowledgeBase?.fileIds || [];
    setSelectedFiles(existingFileIds);
  }, [assistantDetails?.model?.knowledgeBase?.fileIds]);

  useEffect(() => {
    setFileLoading(true);
    getAllFiles({ limit: 100 })
      .then((res) => setFiles(res))
      .catch((e) => console.error(e || 'Error fetching files'))
      .finally(() => setFileLoading(false));
  }, []);

  // Sync Redux provider
  useEffect(() => {
    if (initialProvider && providers?.includes(initialProvider)) {
      setSelectedProvider(initialProvider);
    }
  }, [initialProvider]);

  // Sync Redux model
  useEffect(() => {
    const models = Object.values(LLM_CONFIG[selectedProvider]?.models || {});
    if (initialModel && models?.includes(initialModel)) {
      setSelectedModel(initialModel);
    } else if (models.length > 0) {
      setSelectedModel(models[0]);
    }
  }, [initialModel, selectedProvider]);

  // Change model when provider changes
  useEffect(() => {
    const models = Object.values(LLM_CONFIG[selectedProvider]?.models || []);
    setSelectedModel((prev) => (models.includes(prev) ? prev : models[0]));
  }, [selectedProvider]);

  // Sync sliders from Redux
  useEffect(() => {
    if (typeof reduxModel.temperature === 'number') {
      setTemperature(reduxModel?.temperature);
    }
    if (
      typeof reduxModel?.maxTokens === 'number' &&
      reduxModel?.maxTokens >= 50
    ) {
      setMaxTokens(reduxModel?.maxTokens);
      setMaxTokensInput(String(reduxModel?.maxTokens));
    }
  }, [reduxModel.temperature, reduxModel?.maxTokens]);

  // Dispatch to Redux on change
  useEffect(() => {
    const formattedData = {
      model: {
        provider: selectedProvider,
        model: selectedModel,
        maxTokens,
        temperature,
        knowledgeBase: {
          fileIds: selectedFiles,
          provider: 'google',
        },
        messages: assistantDetails?.model?.messages,
      },
    };
    dispatch(appendToAssistantDetail(formattedData));
  }, [
    selectedProvider,
    selectedModel,
    maxTokens,
    temperature,
    dispatch,
    selectedFiles,
    assistantDetails?.model?.messages,
  ]);

  // === Max Tokens Validation ===
  const validateMaxTokens = () => {
    const val = Number(maxTokensInput);
    if (isNaN(val) || val < 50 || val > 4000) {
      toast.error('Max tokens must be a number between 50 and 4000.');
      setMaxTokensInput(String(maxTokens)); // revert
      return;
    }
    setMaxTokens(val); // valid
  };

  return (
    <div className="card p-20 m-10">
      <div className="mb-10">Choose LLM Model</div>

      <div className="gap-4 flex">
        {/* === Provider Dropdown === */}
        <div style={{ position: 'relative', flex: 1 }}>
          <select
            id="llm-provider"
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            className="agent-form-input flex-1"
            style={{ paddingRight: '2.5rem', appearance: 'none' }}
          >
            {providers.map((prov) => (
              <option key={prov} value={prov}>
                {prov}
              </option>
            ))}
          </select>
          <ChevronDown
            size={20}
            style={{
              position: 'absolute',
              right: '12px',
              top: '40%',
              transform: 'translateY(-50%)',
              color: '#9CA3AF',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* === Model Dropdown === */}
        <div style={{ position: 'relative', flex: 1 }}>
          <select
            id="llm-model"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="agent-form-input flex-1"
            style={{ paddingRight: '2.5rem', appearance: 'none' }}
          >
            {modelEntries.map(([label, val]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={20}
            style={{
              position: 'absolute',
              right: '12px',
              top: '40%',
              transform: 'translateY(-50%)',
              color: '#9CA3AF',
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>

      <p className="agent-form-note mt-4">
        Pick the LLM that will power your agent's reasoning.
      </p>

      {/* === Temperature Slider === */}
      <div className="flex gap-4 items-start mt-10">
        <div className="flex-1">
          <CustomSlider
            label="Temperature"
            initialValue={temperature}
            showPercentage={false}
            onValueChange={setTemperature}
            minRange="0"
            maxRange="1"
          />
          <div className="text-sm m-10">
            Increasing temperature enables creativity but may diverge from the
            prompt.
          </div>
        </div>
      </div>

      {/* === Knowledge Base Chips === */}
      <div className="flex gap-2 items-start mt-3">
        <div className="flex-1">
          <div className="border-bottom mb-10">
            <span className="text-sm w-full mb-1 d-block">
              Knowledge base (Select below files)
            </span>
          </div>

          {fileLoading ? (
            <div
              className="shimmer"
              style={{ width: '100%', height: '40%', borderRadius: '20px' }}
            ></div>
          ) : files?.files?.length > 0 ? (
            <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
              {/* Add File Chip */}
              <div
                className="d-inline-flex align-items-center"
                onClick={() => navigate('/knowledge-base')}
                style={{
                  fontSize: '12px',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '12px',
                    marginRight: '4px',
                  }}
                >
                  +
                </span>
                <span>Add File</span>
              </div>

              {files.files.map((file) => {
                const isSelected = selectedFiles.includes(file.id);
                return (
                  <div
                    key={file.id}
                    className="d-inline-flex align-items-center"
                    style={{
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderRadius: '20px',
                      backgroundColor: isSelected
                        ? 'var(--primary-500)'
                        : '#f0f0f0',
                      color: isSelected ? 'white' : '#333',
                      cursor: 'pointer',
                      maxWidth: '160px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    onClick={() => {
                      setSelectedFiles((prev) =>
                        isSelected
                          ? prev.filter((id) => id !== file.id)
                          : [...prev, file.id]
                      );
                    }}
                  >
                    <span
                      className="text-truncate"
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {file.name}
                    </span>
                    {isSelected && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFiles((prev) =>
                            prev.filter((id) => id !== file.id)
                          );
                        }}
                        className="ms-1"
                        style={{
                          fontSize: '10px',
                          cursor: 'pointer',
                          color: '#fff',
                          lineHeight: 1,
                        }}
                      >
                        &times;
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: '#888' }}>
              No files found. Click "Add File" to upload.
            </div>
          )}
        </div>
      </div>
      {fileSize > 1024 * 1024 && (
        <div style={{ fontSize: '12px', color: 'red', marginTop: '6px' }}>
          Combined size of selected files exceeds 1MB, which may significantly affect call latency and user experience. Consider reducing file sizes.
        </div>
      )}


      {/* === Max Tokens Input === */}
      <div style={{ flex: 1 }} className="mt-10">
        <label className="text-sm mb-10 block">Max Tokens</label>
        <input
          type="number"
          value={maxTokensInput}
          onChange={(e) => setMaxTokensInput(e.target.value)}
          onBlur={validateMaxTokens}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              validateMaxTokens();
            }
          }}
          placeholder="Enter max tokens"
          min={0}
          max={4000}
          style={{
            width: '100%',
            padding: '0.5rem 0.75rem',
            fontSize: '0.875rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div className="text-sm m-10">
          This is the max number of tokens that the assistant will be allowed
          to generate in each turn of the conversation.
          {maxTokens < 50 && (
            <span className="text-red-500 font-semibold">
              {' '}
              (Minimum token limit is 50, so it will automatically be set to
              50.)
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default LLMComponent;
